import { i18n } from "@lingui/core";
import { t } from "@lingui/core/macro";
import dayjs from "dayjs";
import {
  ALL_WEEKDAYS,
  type TimeOffFieldError,
  type TimeOffFormValues,
} from "./timeOffFormValues";

export function toDate(date: string): Date {
  const [year, month, day] = date.split("-").map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1);
}

export function toYmd(date: Date): string {
  return dayjs(date).format("YYYY-MM-DD");
}

export function formatShortDate(date: string): string {
  return dayjs(date).format("D. M.");
}

/** "10. 11.", with the year once it is not this one. */
export function formatDateWithYear(date: string): string {
  return dayjs(date).year() === dayjs().year()
    ? formatShortDate(date)
    : dayjs(date).format("D. M. YYYY");
}

/** 5. 10. 2026 is a Monday; the next six days run through Sunday. */
const A_MONDAY = Date.UTC(2026, 9, 5, 12);

/** From the locale, not the catalog: a one-letter message would share its id with every other "T". */
function weekdayNames(width: "narrow" | "short"): string[] {
  const format = new Intl.DateTimeFormat(i18n.locale || "sl", {
    weekday: width,
    timeZone: "UTC",
  });
  return Array.from({ length: 7 }, (_, index) =>
    format.format(new Date(A_MONDAY + index * 86_400_000)),
  );
}

/** Upper-cased: some locales, Slovenian among them, write them in lower case. */
export function weekdayLetters() {
  return weekdayNames("narrow").map((letter) => letter.toLocaleUpperCase());
}

export function weekdayShortNames() {
  return weekdayNames("short").map((name) => name.replace(/\.$/, ""));
}

function weekdaysOf(mask: number): string {
  const names = weekdayShortNames();
  return names.filter((_, index) => (mask & (1 << index)) !== 0).join(", ");
}

/** The rule in words, as the summary opens with it. The counts are the server's. */
export function describeRepeat(values: TimeOffFormValues): string {
  const days = weekdaysOf(values.weekdayMask);
  switch (values.repeat) {
    case "NONE":
      return t`Se ne ponavlja`;
    case "DAILY":
      return values.weekdayMask === ALL_WEEKDAYS
        ? t`Vsak dan`
        : t`Vsak dan ob ${days}`;
    case "WEEKLY":
      return t`Vsak teden ob ${days}`;
    case "BIWEEKLY":
      return t`Vsak drugi teden ob ${days}`;
    case "CUSTOM": {
      const interval = values.customInterval;
      return t`Vsakih ${interval} tednov ob ${days}`;
    }
    case "MONTHLY": {
      const day = dayjs(values.date).date();
      return t`Vsak mesec na ${day}. dan`;
    }
    case "DATES":
      return t`Na izbrane datume`;
  }
}

/** "1 h 30 min", "45 min" — a type's default length, as its tile advertises it. */
export function formatDuration(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;

  if (hours === 0) return t`${rest} min`;
  if (rest === 0) return t`${hours} h`;
  return t`${hours} h ${rest} min`;
}

/** A server refusal as the form shows it; never the server's own text, which is not written for the user. */
export function timeOffErrorMessage(
  code: string | null | undefined,
  status?: number | null,
): string {
  switch (code) {
    case "NO_TARGETS":
      return t`Nihče izbran v tem obdobju nima izmene ali je že odsoten, zato ni ničesar za dodati.`;
    case "TOO_MANY_TARGETS":
      return t`Naenkrat lahko izberete največ 50 zaposlenih in virov.`;
    case "DUPLICATE_TARGET":
      return t`Isti zaposleni ali vir je izbran večkrat.`;
    case "TARGET_NOT_FOUND":
      return t`Izbranega zaposlenega ali vira ni več.`;
    case "TYPE_NOT_APPLICABLE":
      return t`Te vrste odsotnosti ni mogoče uporabiti za izbrane vire.`;
    case "TYPE_REQUIRED":
      return t`Izberite vrsto odsotnosti.`;
    case "TYPE_ARCHIVED":
      return t`Ta vrsta odsotnosti je arhivirana.`;
    case "TYPE_GRANULARITY_MISMATCH":
      return t`Odsotnost lahko spremenite le v vrsto iste skupine. Za drugo skupino jo izbrišite in dodajte novo.`;
    case "DAILY_LIMIT_EXCEEDED":
      return t`Odsotnost presega dnevno omejitev za to vrsto.`;
    case "TIME_OFF_OVERLAPS":
      return t`Nekateri izbrani so v tem obdobju že odsotni.`;
    case "INVALID_DATE":
      return t`Izbran datum ni veljaven.`;
    case "INVALID_TIME_WINDOW":
      return t`Konec mora biti za začetkom in na isti dan.`;
    case "MULTI_DAY_OCCURRENCE":
      return t`Odsotnost med delovnim časom se mora začeti in končati na isti dan.`;
    case "END_DATE_REQUIRED":
      return t`Izberite, do kdaj naj se odsotnost ponavlja.`;
    case "END_DATE_BEFORE_START":
      return t`Konec mora biti za začetkom.`;
    case "SERIES_TOO_LONG":
      return t`Odsotnost se lahko ponavlja največ 24 mesecev.`;
    case "INVALID_INTERVAL":
      return t`Število tednov med ponovitvami ni veljavno.`;
    case "INVALID_WEEKDAY_MASK":
      return t`Izberite vsaj en dan v tednu.`;
    case "NOT_A_SERIES":
      return t`Ta odsotnost se ne ponavlja, zato jo uredite samo za ta dan.`;
    case "NOT_IN_A_BATCH":
      return t`Ta odsotnost ni bila dodana skupaj z drugimi.`;
    case "SCOPE_NOT_APPLICABLE":
      return t`Te spremembe ni mogoče uporabiti za izbrane ponovitve.`;
    case "SERIES_WINDOW_NOT_UNIFORM":
      return t`Ponovitve imajo različne čase, zato jih uredite posamično.`;
    case "SERIES_DELETED":
      return t`Ponavljanje te odsotnosti je bilo izbrisano.`;
  }
  if (status === 400) return t`Nekateri podatki niso veljavni. Preverite vnos.`;
  if (status === 403) return t`Za to dejanje nimate dovoljenja.`;
  if (status === 404) {
    return t`Odsotnosti ali vrste odsotnosti ni več. Osvežite stran.`;
  }
  return t`Prišlo je do nepričakovane napake`;
}

export function fieldErrorMessage(error: TimeOffFieldError): string {
  switch (error) {
    case "NO_TARGETS":
      return t`Izberite vsaj enega zaposlenega ali vir`;
    case "TOO_MANY_TARGETS":
      return t`Naenkrat lahko izberete največ 50 zaposlenih in virov`;
    case "NO_TYPE":
      return t`Izberite vrsto odsotnosti`;
    case "INVALID_TIME":
      return t`Vnesite veljaven čas`;
    case "INVALID_DATE":
      return t`Vnesite veljaven datum`;
    case "END_BEFORE_START":
      return t`Konec mora biti za začetkom`;
    case "NO_WEEKDAY":
      return t`Izberite vsaj en dan v tednu`;
    case "NO_DATES":
      return t`Izberite vsaj en datum`;
    case "REPEAT_ENDS_BEFORE_START":
      return t`Ponavljanje se mora končati za začetkom`;
    case "INVALID_COMPENSATION":
      return t`Vnesite veljaven odstotek (0–100) ali znesek`;
  }
}
